/**
 * Single source of truth for site-wide constants and content.
 * Copy is real (from the Datamatix content drafts, general-consulting angle):
 * de-personalized voice, no region, no federal references, no published contact
 * details. Update this file to edit most of the site's words.
 */

export const SITE = {
  name: "Datamatix Solutions",
  legalName: "Datamatix Solutions LLC",
  domain: "datamxs.com",
  // Canonical serving host is www (the apex 301-redirects to it at the registrar).
  url: "https://www.datamxs.com",
  tagline: "Modern technology, practical results.",
  description:
    "Datamatix Solutions is an IT consulting firm that modernizes business processes with Microsoft Power Platform, Azure cloud engineering, and business automation.",
  primaryCta: "Schedule a consultation",
} as const;

export type NavItem = { label: string; href: string };

export const NAV: NavItem[] = [
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export type Capability = { label: string; blurb: string };

/** Short capability strip under the hero. */
export const CAPABILITIES: Capability[] = [
  { label: "Power Platform", blurb: "Power Apps, Power Automate, Power BI, Dataverse" },
  { label: "Azure", blurb: "Serverless APIs, data services, integration" },
  { label: "Custom apps", blurb: "Bespoke business applications and modernization" },
  { label: "Automation", blurb: "Python, PowerShell, and browser automation" },
  { label: "Data & analytics", blurb: "Pipelines, dashboards, decision-ready data" },
  { label: "AI-enabled", blurb: "LLMs applied to real workflows, with cost discipline" },
];

export type Service = {
  id: string;
  title: string;
  summary: string;
  outcomes: string[];
};

/** Full service catalogue — anchor-linkable on /services#<id>. */
export const SERVICES: Service[] = [
  {
    id: "power-platform",
    title: "Microsoft Power Platform",
    summary:
      "We design and build custom business applications and automated workflows on Microsoft Power Platform, extending the Microsoft 365 environment your team already uses every day.",
    outcomes: [
      "Power Apps built around your actual processes, not a generic template",
      "Power Automate flows that remove repetitive approvals, notifications, and data movement",
      "Power BI dashboards and operational reporting leaders can act on",
      "Dataverse data modeling with custom plugins and relational structure that scales",
      "Maintenance and enhancement of existing Power Platform applications",
    ],
  },
  {
    id: "azure",
    title: "Azure Cloud Engineering",
    summary:
      "We build serverless, cloud-native solutions on Microsoft Azure — right-sized for small and mid-sized organizations, with no infrastructure you don't need.",
    outcomes: [
      "Azure Functions for serverless APIs, scheduled jobs, and event-driven logic",
      "Azure SQL, Data Factory, and Synapse for durable data and analytics pipelines",
      "Logic Apps and API Management to connect systems cleanly",
      "Hybrid architectures joining Azure, Power Platform, and third-party APIs into one working system",
    ],
  },
  {
    id: "custom-apps",
    title: "Custom Business Applications",
    summary:
      "When off-the-shelf software falls short, we build lightweight, high-performance applications that replace manual processes and disconnected spreadsheets.",
    outcomes: [
      "Requirements analysis and solution architecture before a line of code",
      "Custom application development and modernization of aging systems",
      "Integration with Dataverse, Dynamics 365, and enterprise data platforms",
      "Application lifecycle support — testing, deployment, and long-term maintenance",
    ],
  },
  {
    id: "automation",
    title: "Automation, Scripting & Integration",
    summary:
      "If your team does it by hand every week, we can probably automate it. We build automation in the language that fits the job.",
    outcomes: [
      "Python for data processing, API integrations, and scheduled pipelines (including FastAPI services)",
      "PowerShell for Windows and Microsoft 365 administration and automation",
      "Browser automation with Playwright and Selenium for sites with no API",
      "Excel automation and financial modeling — structured, formula-driven workbooks",
      "Integration across REST APIs, Google APIs, and Microsoft services",
    ],
  },
  {
    id: "data",
    title: "Data Pipelines & Analytics",
    summary:
      "We turn scattered public and internal data into clean, decision-ready datasets — built to run repeatedly, not as one-off scripts.",
    outcomes: [
      "End-to-end pipelines: collection, enrichment, scoring, and storage",
      "Web data collection combining API sources with targeted, well-behaved scraping",
      "Interactive dashboards and executive reporting for visibility into operations",
      "Market and property data analysis with scenario modeling",
    ],
  },
  {
    id: "ai",
    title: "AI-Enabled Solutions",
    summary:
      "We integrate large language models and generative AI into real business workflows — pragmatically, with cost discipline.",
    outcomes: [
      "AI-assisted content and document generation inside automated pipelines",
      "Multi-model pipelines orchestrating text, image, and other generation models",
      "Practical model selection — matching the model to the task and the budget",
    ],
  },
];

export type Step = { title: string; body: string };

/** "How we work" — the numbered spine on the home page. */
export const HOW_WE_WORK: Step[] = [
  {
    title: "Discover & audit",
    body: "We start with data, not opinions — structured discovery and, where useful, automated audits of your current state.",
  },
  {
    title: "Plan first",
    body: "You receive a complete written implementation plan before a single line of code is written. No surprises.",
  },
  {
    title: "Build lean",
    body: "A small, senior team and lightweight architectures — automation wherever it saves you money.",
  },
  {
    title: "Review & iterate",
    body: "Structured checkpoints so the result matches the plan you approved.",
  },
  {
    title: "Launch & maintain",
    body: "Deployment plus optional retainer-based ongoing care so what we build stays fast and secure.",
  },
];

export type Differentiator = { title: string; body: string };

export const DIFFERENTIATORS: Differentiator[] = [
  {
    title: "Senior, hands-on delivery",
    body: "The people you meet are the people who build. No layers of account managers between you and the engineering.",
  },
  {
    title: "Plan-first engagements",
    body: "Full written plans before build. You approve the blueprint, then we execute it.",
  },
  {
    title: "Automation efficiency",
    body: "We run our own firm on tooling we built ourselves — and that efficiency shows up in your invoice.",
  },
  {
    title: "Depth across the stack",
    body: "Microsoft Power Platform and Azure on one side; Python, JavaScript, and modern AI tooling on the other.",
  },
  {
    title: "Evidence-based recommendations",
    body: "We measure before we recommend, so proposals are grounded in your real systems and data.",
  },
  {
    title: "Performance-obsessed builds",
    body: "Lightweight solutions that stay fast and are cheap to run and maintain over time.",
  },
];

export type Faq = { q: string; a: string };

export const FAQS: Faq[] = [
  {
    q: "Do you only work within the Microsoft ecosystem?",
    a: "Microsoft Power Platform and Azure are our foundation, but we pair them with open tooling in Python and JavaScript. We meet you where you are: extending the Microsoft 365 investment you already have, and building custom where off-the-shelf software falls short.",
  },
  {
    q: "What size of engagement do you take on?",
    a: "From a single automated workflow to a multi-part application or data platform. Because we work lean, smaller and mid-sized engagements are a good fit — you get senior attention without enterprise overhead.",
  },
  {
    q: "How do engagements start?",
    a: "With discovery and, where useful, an audit of your current state — then a written implementation plan you approve before we build. You always know the scope and the sequence before work begins.",
  },
  {
    q: "Can you work with our existing Microsoft 365 environment?",
    a: "Yes. Most of our work extends environments already in place — Power Platform apps and flows, Azure services, and integrations that connect the systems your team uses daily.",
  },
  {
    q: "Do you provide support after launch?",
    a: "Yes. Beyond delivery we offer optional retainer-based care covering updates, monitoring, and enhancements, so your applications and automations stay reliable.",
  },
];
